import { ApiError } from './api'

/**
 * KAN-857 U8 — the SPA side of admin step-up (design §5.7, §6.3).
 *
 * The server is the only enforcement point. The SPA only avoids surprising
 * the admin: it asks BEFORE a destructive dialog submits (the status
 * pre-flight) and, if a submit is still refused, keeps the form in memory and
 * offers the step-up — it never replays anything by itself (D7).
 *
 * While `admin_step_up_mode` is absent (the dark default) the pre-flight
 * answers `required: false` and nothing here changes what the admin sees.
 */

/**
 * The server's closed StepUpAction names for the dialogs the SPA gates today
 * (apps/api/src/auth/step-up/actions.ts). Grows as dialogs adopt; a test
 * keeps every name here registered on the server.
 */
export const SPA_STEP_UP_ACTIONS = ['story.merge', 'story.split', 'story.eject'] as const
export type StepUpAction = (typeof SPA_STEP_UP_ACTIONS)[number]

export interface StepUpStatus {
  required: boolean
  active: boolean
  expiresAt: string | null
  unavailable: boolean
}

/** A refusal the admin can fix by stepping up (403 step_up_required). */
export function isStepUpRequired(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 403) return false
  try {
    const body = JSON.parse(err.body) as { error?: unknown }
    return body.error === 'step_up_required'
  } catch {
    return false
  }
}

/** The gate is failing closed server-side (503 step_up_unavailable). */
export function isStepUpUnavailable(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 503) return false
  try {
    return (JSON.parse(err.body) as { error?: unknown }).error === 'step_up_unavailable'
  } catch {
    return false
  }
}

/**
 * Where "Confirm it's you" goes: a top-level navigation to the start
 * endpoint, returning to the current admin page (the server normalises
 * anything outside /admin to /admin anyway).
 */
export function stepUpStartUrl(action: StepUpAction, pathname: string): string {
  const returnTo = /^\/admin(\/|$)/.test(pathname) ? pathname : '/admin'
  const qs = new URLSearchParams({ returnTo, action })
  return `/api/auth/step-up?${qs.toString()}`
}

/** The closed denial reasons the callback may put on `?reason=`. */
const REASON_TEXT: Record<string, string> = {
  step_up_state_invalid: 'The confirmation link expired or was already used.',
  step_up_binding_mismatch: 'The confirmation finished in a different browser or session.',
  step_up_exchange_failed: 'Sign-in did not complete.',
  step_up_subject_mismatch: 'You signed in as a different account.',
  step_up_not_fresh: 'Sign-in did not ask you to authenticate again.',
  step_up_factor_insufficient: 'A second factor is required.',
  step_up_unavailable: 'Confirmation is temporarily unavailable.',
  step_up_session_expiring: 'Your session is about to expire. Sign in again.',
  step_up_actor_not_live: 'Your admin access is no longer active.',
}

/** `?stepup=failed&reason=...` on return, as a fixed message (never the raw value). */
export function readStepUpOutcome(search: string): { failed: boolean; message: string | null } {
  const p = new URLSearchParams(search)
  if (p.get('stepup') !== 'failed') return { failed: false, message: null }
  const reason = p.get('reason') ?? ''
  return { failed: true, message: REASON_TEXT[reason] ?? 'Confirmation did not complete.' }
}
